import React from "react";
import moment from "moment";
import { FormikProps } from "formik";
import { getFile } from "xopsUtils";
import { FilterOperators } from "@crispico/foundation-gwt-js";
import { InvoiceOpenFieldEditor } from "./InvoiceOpenFieldEditor";
import { InvoiceGeneratePdfModal } from "./InvoiceGeneratePdfModal";
import { InvoiceDateFieldDescriptor } from "./InvoiceDateFieldEditor";
import { Button, Checkbox, Form, Icon, Modal } from "semantic-ui-react";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { ModalExt } from "@crispico/foundation-react/components/ModalExt/ModalExt";
import { InvoiceRecalculationModal, RecalculationType } from "./InvoiceRecalculationModal";
import { CrudFormInEditor } from "@crispico/foundation-react/entity_crud/CrudFormInEditor";
import { TabRouterPane } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";
import { IAction } from "@crispico/react-timeline-10000/types/components/ContextMenu/IAction";
import { OverrideableElement } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";
import { EXPORT_AS_PDF_ALL_FILTERED_ROWS, GENERATE_INVOICES, GENERATE_CSV, EXPORT_AS_CSV_ALL_FILTERED_ROWS } from "./queries";
import { ReduxReusableComponents, RRCProps } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { DatePickerReactCalendar } from "@crispico/foundation-react/components/DatePicker/DatePickerReactCalendar/DatePickerReactCalendar";
import { DummyToRememberPeopleToCast, EntityDescriptor, EntityEditorPage, EntityEditorPageProps, EntityTablePage, EntityTablePagePartialProps, EntityTablePageReducers, EntityTablePageState, FieldDescriptor, ITableActionParamForRun, Utils, apolloClientHolder } from "@crispico/foundation-react";

interface GenerateModalOptions {
    showModal: boolean,
    airline: string,
    isAirlineEmpty: boolean,
    isAllAirlinesChecked: boolean,
    startDate: string,
    endDate: string,
    objectOption: ObjectOptions
}

interface RecalculateInvoiceModal {
    type: RecalculationType | undefined,
    entityId: number | undefined,
    showModal: boolean,
}

interface GeneratePDFModal {
    entityId: number | undefined,
    showModal: boolean,
}

export enum ObjectOptions { ALL_OBJECTS = 1, ASSIGNED_OBJECTS = 2, TREATED_OBJECTS = 3 };

class InvoiceTablePageState extends EntityTablePageState {
    generatePdfModal: GeneratePDFModal = {
        entityId: undefined,
        showModal: false
    }

    recalculateInvoiceModal: RecalculateInvoiceModal = {
        type: undefined,
        entityId: undefined,
        showModal: false
    };

    generateModalOptions: GenerateModalOptions = {
        showModal: false,
        airline: "",
        isAirlineEmpty: false,
        isAllAirlinesChecked: false,
        startDate: moment(Utils.now()).subtract(1, 'month').startOf('month').toISOString(),
        endDate: moment(Utils.now()).subtract(1, 'month').endOf('month').toISOString(),
        objectOption: ObjectOptions.TREATED_OBJECTS
    };
}

class InvoiceTablePageReducers<S extends InvoiceTablePageState = InvoiceTablePageState> extends EntityTablePageReducers<S> { }

type InvoiceTablePageProps = EntityTablePagePartialProps & RRCProps<InvoiceTablePageState, InvoiceTablePageReducers>;

class InvoiceTablePage extends EntityTablePage<InvoiceTablePageProps> {

    protected async generateInvoices() {
        if (Utils.isNullOrEmpty(this.props.s.generateModalOptions.airline)) {
            this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, isAirlineEmpty: true } });
            return;
        }
        await apolloClientHolder.apolloClient.mutate({
            mutation: GENERATE_INVOICES,
            variables: {
                airline: this.props.s.generateModalOptions.airline,
                startDate: moment(this.props.s.generateModalOptions.startDate).toDate(),
                endDate: moment(this.props.s.generateModalOptions.endDate).toDate(),
                objectOption: this.props.s.generateModalOptions.objectOption },
            context: { showSpinner: true }
        });
        this.refresh();
        this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, showModal: false } });
    }
    
    protected async exportAsPDFAllFilteredRows() {
        const pdfPath = (await apolloClientHolder.apolloClient.query({
            query: EXPORT_AS_PDF_ALL_FILTERED_ROWS,
            variables: { filter: this.getCustomQueryDefinitionForLoad().filter },
            context: { showSpinner: true }
        })).data.invoiceService_exportAsPDFAllFilteredRows;
        const downloadURL = getFile(pdfPath);
        window.open(downloadURL)
    }

    protected async exportAsCSVAllFilteredRows() {
        const csvPath = (await apolloClientHolder.apolloClient.query({
            query: EXPORT_AS_CSV_ALL_FILTERED_ROWS,
            variables: { filter: this.getCustomQueryDefinitionForLoad().filter },
            context: { showSpinner: true }
        })).data.invoiceService_exportAsCSVAllFilteredRows;
        const downloadURL = getFile(csvPath);
        window.open(downloadURL)
    }

    protected async setGenerateForAllAirlines() {
        await this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, isAllAirlinesChecked: !this.props.s.generateModalOptions.isAllAirlinesChecked} })
        this.props.s.generateModalOptions.isAllAirlinesChecked && this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, airline: "ALL", isAirlineEmpty: false } });
    }

    protected closeInvoiceGeneratorModal() {
        this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, showModal: false, isAirlineEmpty: false, isAllAirlinesChecked: false, airline: "" } });
    }

    protected renderInvoiceGeneratorModal() {
        return <ModalExt size="tiny" open={this.props.s.generateModalOptions.showModal} onClose={() => this.closeInvoiceGeneratorModal()} >
            <Modal.Header><Icon name="file text"/> {_msg("Invoice.generateInvoice.label")}</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field>
                        <label>{_msg("Invoice.airline.label")}</label>
                        <input type="text" style={this.props.s.generateModalOptions.isAirlineEmpty ? { border: '2px solid red' } : undefined} value={this.props.s.generateModalOptions.airline} disabled={this.props.s.generateModalOptions.isAllAirlinesChecked} onChange={(event) => this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, airline: event.target.value } })} />
                    </Form.Field>
                    <Form.Field>
                        <Checkbox label={_msg("InvoiceGeneratorModal.generateForAllAirlines")} checked={this.props.s.generateModalOptions.isAllAirlinesChecked} onChange={() => this.setGenerateForAllAirlines()} />
                    </Form.Field>
                    <Form.Group inline>
                        <label>{_msg("general.period")}</label>
                        <Form.Field className="flex-container">
                            <DatePickerReactCalendar format={Utils.dateFormat} value={moment(this.props.s.generateModalOptions.startDate)} onChange={(startDate) => this.props.r.setInReduxState({ generateModalOptions: {...this.props.s.generateModalOptions, startDate: moment(startDate).toISOString()} })} />
                        </Form.Field>
                        <Form.Field className="flex-container">
                            <DatePickerReactCalendar format={Utils.dateFormat} value={moment(this.props.s.generateModalOptions.endDate)} onChange={(endDate) => this.props.r.setInReduxState({ generateModalOptions: {...this.props.s.generateModalOptions, endDate: moment(endDate).toISOString()} })} />
                        </Form.Field>
                    </Form.Group>
                    <div className="flex-container gap5">
                        <Form.Field>
                            <Checkbox radio name="objectOptionsRadioGroup" label={_msg("Invoice.objectOption.all")} checked={this.props.s.generateModalOptions.objectOption === ObjectOptions.ALL_OBJECTS}
                                onChange={() => this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, objectOption: ObjectOptions.ALL_OBJECTS } })} />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox radio name="objectOptionsRadioGroup" label={_msg("Invoice.objectOption.assigned")} checked={this.props.s.generateModalOptions.objectOption === ObjectOptions.ASSIGNED_OBJECTS}
                                onChange={() => this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, objectOption: ObjectOptions.ASSIGNED_OBJECTS } })} />
                        </Form.Field>
                        <Form.Field>
                            <Checkbox radio name="objectOptionsRadioGroup" label={_msg("Invoice.objectOption.treated")} checked={this.props.s.generateModalOptions.objectOption === ObjectOptions.TREATED_OBJECTS}
                                onChange={() => this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, objectOption: ObjectOptions.TREATED_OBJECTS } })} />
                        </Form.Field>
                    </div>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={() => this.generateInvoices()}>{_msg("InvoiceGeneratorModal.generate")}</Button>
                <Button onClick={() => this.closeInvoiceGeneratorModal()}>{_msg("general.cancel")}</Button>
            </Modal.Actions>
        </ModalExt>
    }

    renderMain() {
        return <>
            {super.renderMain()}
            {this.renderInvoiceGeneratorModal()}
            <InvoiceRecalculationModal showModal={this.props.s.recalculateInvoiceModal.showModal} 
                type={this.props.s.recalculateInvoiceModal.type} entityId={this.props.s.recalculateInvoiceModal.entityId} 
                onClose={() => this.props.r.setInReduxState({ recalculateInvoiceModal: { ...this.props.s.recalculateInvoiceModal, showModal: false } })} />
            <InvoiceGeneratePdfModal showModal={this.props.s.generatePdfModal.showModal} entityId={this.props.s.generatePdfModal.entityId} onClose={() => this.props.r.setInReduxState({ generatePdfModal: { ...this.props.s.generatePdfModal, showModal: false } })} />
        </>
    }

    protected provideActionsForCell(actionParam: ITableActionParamForRun): IAction[] {
        const isInvoiceOpen = this.getEntityAt(actionParam.selection[0]).open;
        return isInvoiceOpen ? super.provideActionsForCell(actionParam) : []; 
    }

    protected provideActionsForRow(actionParam: ITableActionParamForRun): IAction[] {
        let actions = super.provideActionsForRow(actionParam);
        const entitySelected = this.getEntityAt(actionParam.selection[0]);
        actions.push(...[{
            icon: "redo",
            label: _msg("Invoice.recalculateBasedOnTask.label"),
            isDisabled: () => entitySelected && !entitySelected.open, 
            run: () => {
                this.props.r.setInReduxState({ recalculateInvoiceModal: { ...this.props.s.recalculateInvoiceModal, showModal: true, type: RecalculationType.USING_TASKS, entityId: entitySelected?.id } });
            }
        },
        {
            icon: "redo", 
            label: _msg("Invoice.recalculateBasedOnLines.label"),
            isDisabled: () => entitySelected && !entitySelected.open,
            run: () => {
                this.props.r.setInReduxState({ recalculateInvoiceModal: { ...this.props.s.recalculateInvoiceModal, showModal: true, type: RecalculationType.USING_INVOICE_LINES, entityId: entitySelected?.id } });
            }
        },
        {
            icon: "redo",
            label: _msg("Invoice.recalculateBasedOnLinesAndData.label"),
            isDisabled: () => entitySelected && !entitySelected.open,
            run: () => {
                this.props.r.setInReduxState({ recalculateInvoiceModal: { ...this.props.s.recalculateInvoiceModal, showModal: true, type: RecalculationType.USING_INVOICE_DATA, entityId: entitySelected?.id } });
            }
        },
        {
            icon: "print",
            label: _msg("Invoice.exportPdf.label"),
            run: () => {
                this.props.r.setInReduxState({ generatePdfModal: { ...this.props.s.generatePdfModal, showModal: true, entityId: entitySelected?.id } });
            }
        },
        {
            icon: "print",
            label: _msg("Invoice.exportCsv.label"),
            run: async () => {
                const csvPath: string = (await apolloClientHolder.apolloClient.query({ query: GENERATE_CSV, variables: { invoiceId: entitySelected.id }, context: { showSpinner: true } })).data.invoiceService_generateCSV;
                const downloadURL = getFile(csvPath);
                window.open(downloadURL)
            }
        }]);
        return actions;
    }

    protected preRenderButtons(params: {}): Array<OverrideableElement> {
        return [
            ...super.preRenderButtons(params),
            { element: <Button key="generateModalOptions" color="green" onClick={() => this.props.r.setInReduxState({ generateModalOptions: { ...this.props.s.generateModalOptions, showModal: true } })}>{_msg("Invoice.generateInvoice.label")}</Button> },
            { element: <Button key="exportAsCSVAll" onClick={() => this.exportAsCSVAllFilteredRows()}>{_msg("Invoice.exportAsCsvAllFilteredRows.label")}</Button> },
            { element: <Button key="exportAsPDFAll" onClick={() => this.exportAsPDFAllFilteredRows()}>{_msg("Invoice.exportAsPdfAllFilteredRows.label")}</Button> }
        ];
    }

}

const InvoiceTablePageRRC = ReduxReusableComponents.connectRRC(InvoiceTablePageState, InvoiceTablePageReducers, InvoiceTablePage);

export class InvoiceEntityDescriptor extends EntityDescriptor {
    constructor() {
        super({
            name: "Invoice",
            icon: "file text",
            defaultFilter: Filter.createForClient("endDate", FilterOperators.forDate.lastMonth)
        });
    }

    protected customize() {
        this.addFieldDescriptor({ name: "airline", enabled: false });
        this.addFieldDescriptor({ name: "totalAmount", enabled: false });
        this.addFieldDescriptor({ name: "destination", type: FieldType.text });
        this.addFieldDescriptor({ name: "contractNumber", type: FieldType.string, enabled: false });
        this.addFieldDescriptor({ name: "open", type: FieldType.boolean }, new class extends FieldDescriptor {
            renderFieldEditor(formikProps: FormikProps<any>, additionalFieldEditorProps?: DummyToRememberPeopleToCast) {
                return <InvoiceOpenFieldEditor formikProps={formikProps} fieldName={this.name} />;
            }
        });
        this.addFieldDescriptor({ name: "objectOption", type: FieldType.dropdown, fieldDescriptorSettings: {
            fieldIntervals: [
                { from: ObjectOptions.ALL_OBJECTS, label: _msg("Invoice.objectOption.all") },
                { from: ObjectOptions.ASSIGNED_OBJECTS, label: _msg("Invoice.objectOption.assigned") },
                { from: ObjectOptions.TREATED_OBJECTS, label: _msg("Invoice.objectOption.treated") }]
             }
        });
        this.addFieldDescriptor({ name: "startDate", additionalFieldEditorProps: { isStartOfDay: true }, additionalFieldRendererProps: { format: Utils.dateFormat } }, new InvoiceDateFieldDescriptor());
        this.addFieldDescriptor({ name: "endDate", additionalFieldRendererProps: { format: Utils.dateFormat } }, new InvoiceDateFieldDescriptor());
        this.addFieldDescriptor({ name: "facturationDate", additionalFieldEditorProps: { isStartOfDay: true }, additionalFieldRendererProps: { format: Utils.dateFormat } }, new InvoiceDateFieldDescriptor());
        this.infoEditor.wrappedComponentClass = class extends EntityEditorPage<EntityEditorPageProps> {
            protected getExtraTabPanes(): (TabRouterPane | null)[] {
                return [
                    ...super.getExtraTabPanes(),
                    this.createOneToManyTabPane("InvoiceLine", "invoice"),
                    this.createOneToManyTabPane("InvoiceData", "invoice")
                ];
            }

            protected renderForm() {
                return React.createElement(InvoiceFormInEditor, this.getPropsForFormSimple());
            }
        }
    }

    renderTable() {
        return <InvoiceTablePageRRC {...super.renderTable().props} ref={this.entityTablePage} />;
    }
}

class InvoiceFormInEditor extends CrudFormInEditor {
    protected isFieldEnabled(fieldDescriptor: FieldDescriptor) {
        const excludedFields = ["destination", "duplicate", "printInvoiceData"];
        if (this.props.entity.open === false && !excludedFields.includes(fieldDescriptor.name)) {
            return false;
        }
        return super.isFieldEnabled(fieldDescriptor);
    }
}